/**
 * Contacts & CRM.
 *
 * THE BANNER IS LOAD-BEARING, not decoration. A reader arriving at a screen
 * called "CRM" brings expectations from Artlogic, and two of them are wrong
 * here: that they can look somebody up, and that a name in the list is a
 * verified party. The lead answers the first and the section headings answer the
 * second, which is why the named section carries a whole sentence of its own
 * rather than a badge.
 *
 * NO "PROSPECT", NO "VIP", NO STATUS. Every word in this file describes a
 * relationship the database can prove. A status column would be a stored opinion
 * wearing a fact's clothes, and it belongs on the gallery's own note — a table
 * that does not exist yet, which is why nothing here promises one.
 *
 * AND NO CLAIM THE PRODUCT CANNOT KEEP. The stamp copy below says what a reader
 * stamp and a watermark DO — identify the account, and leave a trace — and does
 * not say that content cannot be copied or captured, because it can. A deleted
 * sentence in `.lavish/gallery-contacts-crm.html` is the record of why: "a
 * notice that promises a control which does not exist is worse than no notice".
 * OND-106 owns that wording everywhere else it appears.
 */
import { CONTACT_KIND_LABELS } from "@ondo/shared";

export const CONTACTS_COPY = {
  loading: "Loading your contacts…",
  failed: "Could not load your contacts. Try again in a moment.",
  /**
   * REWORDED BY OND-61, because the old sentence stopped being true.
   *
   * It read "This account holds no gallery seat, so it has no contacts to
   * show", which was right while every active seat could read the whole list.
   * Since the collector list went behind the middle rung and behind a named
   * role's bundle, a 403 here has two causes — no seat at all, and a seat whose
   * abilities do not include the collector list — and the old wording told a
   * Scholar something false about their own employment.
   *
   * It does not say WHICH of the two, and that is not vagueness: the database
   * answers both with one sentence on purpose, and a screen that guessed would
   * be inventing a distinction the server refused to draw. It names the thing
   * to do about it instead, which is the only part the reader can act on.
   */
  forbidden:
    "The collector list is not part of this seat's abilities. A director or registrar at your organisation can change that.",

  lead: "Nobody here was looked up.",
  leadDetail:
    "Every contact below is on this list because a durable record names both of you — a consignment, a sale, a mandate, a covenant, a verification request. ONDO has no directory and no name search: a contact appears when an act creates one, and stays after it ends.",

  /** Filters over the loaded rows. A chip row, never tabs and never a query. */
  filterLabel: "Relationship",
  all: "All",

  /**
   * The relationship a row holds, in the reader's words.
   *
   * IT MOVED TO `@ondo/shared` IN OND-67, and this key is what is left of it.
   * The collector-list export is built on the SERVER now and the exported
   * file's "Type" column is this label, so the map has to be somewhere both
   * can read: one definition, so a kind added in a migration reaches the screen
   * and the file together rather than reaching one of them.
   *
   * `describeKind` in `contacts.model.ts` goes to `describeContactKind` rather
   * than through here — including the fallback to the raw kind, for the reason
   * the wire type gives: a verb added in SQL must not be a compile error in the
   * browser. This key is kept because a reader looking up what this screen
   * calls a relationship will look in this file first, and finding nothing
   * would read as the words having been lost rather than moved.
   */
  kinds: CONTACT_KIND_LABELS,

  /**
   * The column headers. One row of them per section, because the two sections
   * are still the screen's one real claim — the redesign changes what a row
   * SHOWS, not what it is.
   */
  columns: {
    contact: "Contact",
    type: "Type",
    location: "Location",
    heldNow: "Held now",
    historic: "Historic handled",
    verified: "Verified",
    revenue: "Revenue received",
    last: "Last activity",
  },

  /**
   * Search. A filter over the rows already on screen — never a query. The
   * placeholder says so, because a directory is the one thing this box must
   * never be mistaken for.
   */
  searchLabel: "Search contacts",
  searchPlaceholder: "Search name, email or ONDO id…",
  searchNote: "Filters the rows below — never a directory.",

  /**
   * What stands where an ONDO id would be.
   *
   * A sentence rather than a dash, because the absence is the most important
   * thing about the row: there is no id, none was ever attached, and nothing on
   * this screen can attach one.
   */
  namedMeta: "no ONDO id · as you recorded it",

  /** The two sections, and the sentence that keeps them apart. */
  attachedTitle: "On ONDO",
  attachedNote:
    "Registry identities. Every act you share with them is under one id, and the count beside each name is what the registry can prove.",
  namedTitle: "Named on your own records — not ONDO accounts",
  namedNote:
    "Names you typed on your own sales, and artists named on a covenant. ONDO has checked none of them and they resolve to nobody. Rows are grouped where the text matches exactly, which is your reading of your own records rather than the registry's — two identical names may be two people.",

  /** Tiles. Counts over the list below them, never a trend. */
  tileContacts: "On ONDO",
  tileNamed: "Named only",
  tileLive: "Live relationships",

  emptyLead: "No contacts yet.",
  emptyDetail:
    "Accept a consignment, record a sale, or send a work out to an agent, and whoever is on the other side of it appears here.",

  /**
   * Named where the reader will look for it, because its absence is a decision
   * rather than a gap in the data. Sharing a collection is the one gallery act
   * that produces no contact: the grant row is readable and the principal on it
   * is not, so there is nobody to name.
   */
  sharingNote:
    "People you have shared a collection with are not listed. That read does not exist yet — it is a decision about collections, not one this screen should take.",

  /** Export. What is on screen, filter and all — built and recorded by the server. */
  exportCsv: "↓ Export CSV",
  exporting: "Preparing your file…",
  /**
   * The server's own sentence is shown when there is one. This is the fallback,
   * and it says the thing that is always true of a refused export: no file was
   * released, so there is nothing to wonder about.
   */
  exportFailed:
    "The export was not released. Nothing was downloaded — try again in a moment.",

  /**
   * The reader stamp.
   *
   * SHOWN AT THE TOP OF THE SCREEN, not in a footer, because it is addressed to
   * the person reading: this list is confidential, and who is looking at it is
   * recorded. `stampNote` is the honest claim and the limit of it in one
   * sentence — the exported file carries the same words.
   */
  stampRead: "Read",
  stampNote:
    "This list is confidential. Your name, ONDO id and the time are shown on it and are printed inside anything you export, and every export is recorded. Stamps and watermarks help trace a copy back to the account that took it — they do not prevent copying.",
  stampExportsLink: "Exports from this list",

  /**
   * The note.
   *
   * `notePrivate` is shown WHILE TYPING rather than once in a banner, because
   * this is the only place in ONDO where somebody writes about another principal
   * in a way that principal can never see or answer. A reader should be told
   * that at the moment they are doing it.
   */
  noteAdd: "Add a note",
  noteEdit: "Edit note",
  noteRemove: "Remove",
  noteSave: "Save note",
  noteCancel: "Cancel",
  noteLabel: "Your note",
  notePlaceholder: "Call before Basel. Buys late Currin only.",
  tagsLabel: "Tags, comma separated",
  tagsPlaceholder: "vip, museum, slow payer",
  notePrivate:
    "Private to this gallery. It reaches no feed, no work record and no notification, and the person it is about can never read it or answer it.",

  /** The drawer. */
  timelineFailed: "Could not load that history.",
  timelineEmpty: "Nothing shared with this contact yet.",
  close: "Close",
  spendNone: "—",

  /**
   * The add-contact modal.
   *
   * `addOnSubtitle` states the no-name-search rule the way AssignAgentDialog
   * does — once, here, rather than letting somebody discover it by typing.
   * `addOffSubtitle` says what an email DOES, because it is not a mailing
   * field: it links the contact to an ONDO identity — and deliberately does
   * not say whether that identity already existed, because the server will
   * not say either.
   */
  addContact: "+ Add contact",
  addTitle: "Add a contact",
  addModeOn: "On ONDO",
  addModeOff: "Not on ONDO",
  addOnSubtitle:
    "Type the ONDO id they gave you. There is no name search — an ONDO id is attached, never discovered.",
  addOffSubtitle:
    "Your own record, in your own words. Add an email and the contact is linked to an ONDO identity that becomes theirs if they ever join — you will not be told whether the address is on ONDO already.",
  addIdLabel: "ONDO id",
  addIdPlaceholder: "ONDO-0000",
  addNameLabel: "Name",
  addNamePlaceholder: "Anna Herzog",
  addEmailLabel: "Email — optional",
  addEmailPlaceholder: "anna@example.com",
  addLocationLabel: "Location — optional",
  addLocationPlaceholder: "Geneva, CH",
  addKindLabel: "Type — optional",
  /**
   * Suggestions for a CLAIM, not a vocabulary: `kind_text` is free text in
   * SQL, and these are only the words a firm most likely wants. "Type" here is
   * the firm's own label — never the registry's capacity.
   */
  addKindOptions: ["Collector", "Gallery", "Institution", "Museum", "Advisor"],
  addKindNone: "—",
  addMalformed: "That is not an ONDO id — it looks like ONDO-0000.",
  addPrivate:
    "Private to this firm. Nobody is notified, and the person you add can never see that they are on this list.",
  addSubmit: "Add contact",
  addSubmitting: "Adding…",
  addCancel: "Cancel",
  addedRemove: "Remove contact",
  verifiedMark: "✓",
} as const;
